import React, { useState, useEffect } from 'react';
import Pagination from './Pagination';
import { useAuth } from './AuthContext';
import { groupByCustomer, formatTimestamp, isCritical, localDateStr, QUERY_TYPE_LABELS } from './callUtils';
import { DownloadIcon, SearchIcon } from './Icons';
import { downloadCsv } from './csvExport';
import RecordingPlayer from './RecordingPlayer';

const DEFAULT_PAGE_SIZE = 50;

// Every customer profile saved from the call-history chat modal (name,
// query, new/existing), lined up against that number's most recent call so
// an admin can see who asked what without opening each chat individually.
function QueryPanel({ calls, initialSearch }) {
  const { authFetch } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Lazy initializer: this component remounts fresh every time the Query tab
  // becomes active (see App.js), so this only ever picks up a drill-down
  // filter from Agent Details, never overwrites a search the user is mid-typing.
  const [search, setSearch] = useState(() => initialSearch || '');

  const fetchCustomers = async () => {
    try {
      const response = await authFetch('/api/customers');
      const data = await response.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customer profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const groupedCalls = groupByCustomer(calls);
  const callsByNumber = new Map(groupedCalls.map((group) => [group.customerNumber, group.latestCall]));
  const groupsByNumber = new Map(groupedCalls.map((group) => [group.customerNumber, group]));

  // query/query_type now live on the call they were saved against (see
  // CallHistoryModal), not on the customer row. A missed call rarely gets its
  // own query logged before this panel needs to reflect it, so don't let a
  // query-less missed call blank out a query saved against that customer's
  // last real call — walk back through their history to the most recent call
  // that actually has saved query data, instead of always the true latest call.
  const queryCallByNumber = new Map(
    groupedCalls.map((group) => [
      group.customerNumber,
      group.callsNewestFirst.find((call) => call.query || call.query_type) || null,
    ])
  );

  const savedRows = customers.filter((customer) => {
    const queryCall = queryCallByNumber.get(customer.number);
    return customer.name || customer.is_new || queryCall?.query || queryCall?.query_type;
  });

  const searchTerm = search.trim().toLowerCase();
  const rows = !searchTerm
    ? savedRows
    : savedRows.filter((customer) => {
        const call = callsByNumber.get(customer.number);
        const queryCall = queryCallByNumber.get(customer.number);
        return (
          (customer.name || '').toLowerCase().includes(searchTerm) ||
          customer.number.toLowerCase().includes(searchTerm) ||
          (customer.order_number || '').toLowerCase().includes(searchTerm) ||
          (queryCall?.query || '').toLowerCase().includes(searchTerm) ||
          (QUERY_TYPE_LABELS[queryCall?.query_type] || '').toLowerCase().includes(searchTerm) ||
          (call?.answered_agent_name || '').toLowerCase().includes(searchTerm)
        );
      });

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  const handleExportCsv = () => {
    const headers = [
      'Customer Name',
      'Number',
      'Order Number',
      'Query Type',
      'Query',
      'New / Old',
      'Call Start Time',
      'Agent Name',
      'Recording URL',
      'Query Created At',
    ];
    const exportRows = rows.map((customer) => {
      const call = callsByNumber.get(customer.number);
      const queryCall = queryCallByNumber.get(customer.number);
      return [
        customer.name || '',
        customer.number,
        customer.order_number || '',
        QUERY_TYPE_LABELS[queryCall?.query_type] || '',
        queryCall?.query || '',
        customer.is_new === 'yes' ? 'New' : customer.is_new === 'no' ? 'Old' : '',
        call ? formatTimestamp(call.start_stamp) : '',
        call?.answered_agent_name || '',
        call?.recording_url || '',
        queryCall?.query_updated_at ? formatTimestamp(queryCall.query_updated_at) : '',
      ];
    });
    downloadCsv(`query-panel-${localDateStr(new Date())}.csv`, headers, exportRows);
  };

  if (loading) {
    return <div className="loading-state">Loading…</div>;
  }

  return (
    <>
      <div className="incoming-filters-bar">
        <div className="search-input-wrap">
          <SearchIcon className="search-input-icon" width={15} height={15} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, number, order number, query, or agent"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search saved customer queries"
          />
        </div>
        <button type="button" className="btn btn-secondary export-csv-btn" onClick={handleExportCsv}>
          <DownloadIcon width={14} height={14} />
          Export CSV
        </button>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Number</th>
              <th>Order Number</th>
              <th>Query Type</th>
              <th>Query</th>
              <th>New / Old</th>
              <th>Call Start Time</th>
              <th>Agent Name</th>
              <th>Recording</th>
              <th>Query Created At</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center' }}>
                  {savedRows.length === 0 ? 'No customer queries saved yet.' : 'No matching customer queries found.'}
                </td>
              </tr>
            ) : (
              pageRows.map((customer) => {
                const call = callsByNumber.get(customer.number);
                const queryCall = queryCallByNumber.get(customer.number);
                const group = groupsByNumber.get(customer.number);
                const critical = group ? isCritical(group) : false;
                return (
                  <tr key={customer.number} className={critical ? 'row-critical' : ''}>
                    <td className="table-cell-strong">{customer.name || '-'}</td>
                    <td>
                      {customer.number}
                      {critical && (
                        <span className="status critical table-critical-badge">Critical</span>
                      )}
                    </td>
                    <td>{customer.order_number || '-'}</td>
                    <td>{QUERY_TYPE_LABELS[queryCall?.query_type] || '-'}</td>
                    <td className="table-cell-truncate" title={queryCall?.query || ''}>
                      {queryCall?.query || '-'}
                    </td>
                    <td>
                      {customer.is_new ? (
                        <span className={`status ${customer.is_new === 'yes' ? 'answered' : 'attempted'}`}>
                          {customer.is_new === 'yes' ? 'New' : 'Old'}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{call ? formatTimestamp(call.start_stamp) : '-'}</td>
                    <td>{call?.answered_agent_name || '-'}</td>
                    <td>
                      <RecordingPlayer url={call?.recording_url} />
                    </td>
                    <td>{queryCall?.query_updated_at ? formatTimestamp(queryCall.query_updated_at) : '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        totalItems={rows.length}
      />
    </>
  );
}

export default QueryPanel;
